import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import Swal from "sweetalert2";

// Images
import log1 from "../assets/img/Login-image.png";
import log2 from "../assets/img/login-img-2.png";
import log3 from "../assets/img/login-security.png";
import log4 from "../assets/img/Review Manage.png";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  
  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: ""
  });
  
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Images for carousel
  const images = [
      { src: log1, alt: "Customer review" },
      { src: log2, alt: "Chat interface" },
      { src: log3, alt: "Login Security" },
      { src: log4, alt: "Review Manage" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!passwords.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (passwords.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    }
    
    if (!passwords.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (passwords.newPassword !== passwords.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/reset-password`,
        {
          token,
          newPassword: passwords.newPassword
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "$2a$12$tXgoc0t8Pfva7LmlyQ6/aOtB//vaEWOcdV3ElI82pAlevLYSLeg6W",
          },
        }
      );

      if (response.status === 200) {
        Swal.fire({
          icon: "success",
          title: "Password reset successfully!",
          text: "You can now login with your new password",
          showConfirmButton: false,
          timer: 1500
        });
        navigate("/login");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to reset password"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="epicx-login-container">
      {/* Left Side - Carousel */}
      <div className="epicx-login-carousel">
        <div className="epicx-carousel-slide">
          <img 
            src={images[currentImageIndex].src} 
            alt={images[currentImageIndex].alt} 
          />
        </div>
        <div className="epicx-carousel-dots">
          {images.map((_, index) => (
            <button
              key={index}
              className={`epicx-dot ${index === currentImageIndex ? "epicx-active" : ""}`}
              onClick={() => setCurrentImageIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="epicx-login-form-container">
        <div className="epicx-form-header">
          <h2>Reset Your Password</h2>
          <p>Create a new password for your account</p>
        </div>

        <form onSubmit={handleSubmit} className="epicx-login-form">
          <div className="epicx-form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="epicx-password-input">
              <input
                type={showPassword.newPassword ? "text" : "password"}
                id="newPassword"
                name="newPassword"
                value={passwords.newPassword}
                onChange={handleChange}
                className={errors.newPassword ? "epicx-error" : ""}
                placeholder="Enter new password"
              />
              <button 
                type="button" 
                onClick={() => togglePasswordVisibility("newPassword")}
                className="epicx-toggle-password"
                aria-label={showPassword.newPassword ? "Hide password" : "Show password"}
              >
                <FontAwesomeIcon icon={showPassword.newPassword ? faEyeSlash : faEye} />
              </button>
            </div>
            {errors.newPassword && (
              <span className="epicx-error-message">{errors.newPassword}</span>
            )}
          </div>

          <div className="epicx-form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="epicx-password-input">
              <input
                type={showPassword.confirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={passwords.confirmPassword}
                onChange={handleChange}
                className={errors.confirmPassword ? "epicx-error" : ""}
                placeholder="Confirm new password"
              />
              <button 
                type="button" 
                onClick={() => togglePasswordVisibility("confirmPassword")}
                className="epicx-toggle-password"
                aria-label={showPassword.confirmPassword ? "Hide password" : "Show password"}
              >
                <FontAwesomeIcon icon={showPassword.confirmPassword ? faEyeSlash : faEye} />
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="epicx-error-message">{errors.confirmPassword}</span>
            )}
          </div>

          <button 
            type="submit" 
            className="epicx-submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="epicx-spinner"></span> Resetting Password...
              </>
            ) : (
              "Reset Password"
            )}
          </button>
        </form>

        <div className="epicx-form-footer">
          <p>
            Remember your password?{" "}
            <a href="/login" className="epicx-register-link">Log in</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;